import React, { useEffect, useMemo, useState } from 'react';
import { DollarSign, Search, Download, Save, RefreshCw, Edit, Trash2, AlertTriangle, X, CheckCircle, List } from 'lucide-react';
import MainService from '../../services/MainService';
import { loadAllGridRows } from '../../services/dataSource';
import DataColumnMenu from '../ui/DataColumnMenu';
import { processClientRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { exportGroupedExcel } from '../../utils/excelExport';

const EXPENSE_COLUMNS = [
  { field: 'name', title: 'Name', widthClass: 'w-[40%]', hideable: true },
  { field: 'isForDriver', title: 'Is For Driver', widthClass: 'w-[30%]', hideable: true },
  { field: 'actions', title: 'Options', widthClass: 'w-[20%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultExpenseVisibility = () =>
  EXPENSE_COLUMNS.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const Expenses = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(defaultExpenseVisibility);
  const [groupField, setGroupField] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    isForDriver: false,
  });

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadRows = async () => {
    try {
      setLoading(true);
      const { data } = await loadAllGridRows(MainService.ExpensesInGrid);
      const list = data.map((x, i) => ({
        id: x.Id || i + 1,
        name: x.Name || '',
        isForDriverFlag: !!x.IsForDriver,
        isForDriver: x.IsForDriver ? 'Yes' : 'No',
      }));
      setRows(list);
    } catch (err) {
      console.error('Load expenses error:', err);
      setError('Failed to load expenses');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const groupedRows = useMemo(
    () =>
      processClientRows({
        rows,
        searchTerm,
        searchFields: ['name', 'isForDriver'],
        filtersByField,
        sortField,
        sortDirection,
        groupField,
      }),
    [rows, searchTerm, filtersByField, sortField, sortDirection, groupField]
  );

  const flatFilteredCount = useMemo(
    () => groupedRows.reduce((sum, g) => sum + g.rows.length, 0),
    [groupedRows]
  );

  const colSpan = EXPENSE_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1;

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => groupedRows.filter((g) => g.label != null).map((g) => g.key),
    [groupedRows]
  );

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setFormData({ name: '', isForDriver: false });
    setEditing(null);
  };

  const handleRefresh = async () => {
    resetForm();
    setSearchTerm('');
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await loadRows();
    } catch (err) {
      console.error('Error refreshing expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Enter Name');
      return;
    }
    try {
      setLoading(true);
      const payload = { Name: formData.name.trim(), IsForDriver: !!formData.isForDriver };
      if (editing?.id) payload.Id = editing.id;
      const resp = await MainService.SaveExpenses(payload);
      if (resp === true || resp === 'true' || resp === 'Success') {
        setSuccess(editing ? 'Expense updated successfully' : 'Expense created successfully');
        await loadRows();
        resetForm();
      } else {
        setError('Failed to save expense');
      }
    } catch (err) {
      console.error('Save expense error:', err);
      setError('Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({
      name: row.name || '',
      isForDriver: !!row.isForDriverFlag,
    });
    setError('');
    setSuccess('');
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      setLoading(true);
      setDeleteDialogOpen(false);
      const resp = await MainService.DeleteExpense(rowToDelete.id);
      if (resp === 'Success' || resp === true || resp === 'true') {
        setSuccess('Expense deleted successfully');
        await loadRows();
      } else {
        setError(resp || 'Failed to delete expense');
      }
    } catch (err) {
      console.error('Delete expense error:', err);
      setError('Failed to delete expense');
    } finally {
      setLoading(false);
      setRowToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = EXPENSE_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    setFiltersByField((prev) => ({ ...prev, [field]: filter }));
  };

  const handleClearColumnFilter = (field) => {
    setFiltersByField((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleExportExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: 'Expenses',
        fileName: `expenses_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['Serial No', 'Name', 'Is For Driver'],
        groups: groupedRows,
        groupFieldTitle: EXPENSE_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (row, serial) => [serial, row.name, row.isForDriver],
      });
      setSuccess('Expenses data exported successfully');
    } catch (err) {
      console.error('Error exporting data:', err);
      if (err?.message === 'No rows to export') {
        setError('No expenses to export');
      } else {
        setError('Failed to export data');
      }
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <DollarSign className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">Manage expense types</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-3">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                placeholder=""
              />
            </div>

            <div className="col-span-3 flex items-center gap-2">
              <input
                type="checkbox"
                id="isForDriver"
                name="isForDriver"
                checked={formData.isForDriver}
                onChange={handleInputChange}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label htmlFor="isForDriver" className="text-sm font-medium text-gray-700">
                Is For Driver?
              </label>
            </div>

            <div className="col-span-3 flex items-center gap-2">
              <button
                type="submit"
                disabled={loading}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={editing ? 'Update' : 'Save'}
              >
                {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <List className="w-5 h-5 text-primary mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Expenses List</h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-8 pr-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent w-64"
              />
            </div>
            <button
              onClick={handleExportExcel}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              title="Export Excel"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="master-table-scroll">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 backdrop-blur-sm z-20 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}
          {groupField && (
            <GroupByBanner
              groupTitle={EXPENSE_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={() => setGroupField(null)}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}
          <table className="w-full table-fixed">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="w-[10%] px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  #
                </th>
                {EXPENSE_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={EXPENSE_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={setGroupField}
                      onUngroup={() => setGroupField(null)}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {flatFilteredCount === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-6 text-center text-gray-500">
                    No expenses found
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return groupedRows.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={EXPENSE_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((row) => {
                      serial += 1;
                      block.push(
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">{serial}</td>
                          {columnVisibility.name !== false && (
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">{row.name}</td>
                          )}
                          {columnVisibility.isForDriver !== false && (
                            <td className="whitespace-nowrap px-4 py-2">
                              <span
                                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold leading-5 ${
                                  row.isForDriverFlag
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {row.isForDriver}
                              </span>
                            </td>
                          )}
                          {columnVisibility.actions !== false && (
                            <td className="whitespace-nowrap px-4 py-2 text-center text-sm font-medium">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => handleEdit(row)}
                                  className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                                  title="Edit"
                                  type="button"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(row)}
                                  className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                                  title="Delete"
                                  type="button"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Total Expenses: {rows.length}</span>
            <span>
              Showing {flatFilteredCount} of {rows.length} expenses
              {groupField
                ? ` · grouped by ${EXPENSE_COLUMNS.find((c) => c.field === groupField)?.title || groupField}`
                : ''}
            </span>
          </div>
        </div>
      </div>

      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 rounded-t-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">Confirm Delete</h3>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete the expense <strong>&quot;{rowToDelete?.name}&quot;</strong>?
              </p>
              <p className="text-sm text-gray-500">
                This action cannot be undone and will permanently remove the expense from the system.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
